import CustomForm from '@components/form/CustomForm';
import PageWrapper from '@components/layouts/PageWrapper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supplierFields } from '@helpers/fields/supplier/supplier.fields';
import { useNavigate } from 'react-router-dom';
import { useCreateSupplierMutation } from '@states/supplier/supplier.api';
import { useErrorHandling } from '@hooks/useErrorHandling';
import { castTypeArr } from '@utils/general/cast-type';
import { TFailResponse } from '@type-defs/general/TFailResponse';
import { removeKeys } from '@utils/general/removeKeys';
import { TSuccessResponse } from '@type-defs/general/TSuccessResponse';
import { Toast } from '@helpers/popups/Toast';
import { SUPPLIER } from '@utils/constants/supplier.constant';
import { useGetAllStatesQuery } from '@states/common/common.api';
import {
    addCities,
    addStates,
    addTownships,
} from '@states/common/common.slice';

import { useSelector } from 'react-redux';
import { RootState } from '@states/store';
import { supplierSchema } from '@helpers/validation-schemas/supplier/supplier.schema';
import { LoadingSpinner } from '@components/layouts/LoadingSpinner';
import useRegionEffect from '@hooks/distribution-region/useRegionEffect';

const SupplierAddPage = () => {
    const navigate = useNavigate();
    const {
        cities,
        townships,
        isLoading: regionLoading,
    } = useRegionEffect();

    const [createSupplier, { isLoading, isSuccess, error: createError }] =
        useCreateSupplierMutation();
    const { data: stateData } = useGetAllStatesQuery();

    const {
        states: storedStates,
        cities: storedCities,
        townships: storedTownships,
    } = useSelector((state: RootState) => state.common);

    useErrorHandling(...castTypeArr<TFailResponse>([createError]));

    
    

    const fields = useMemo(() => {
        return supplierFields({
            // stored data
            stateData,
            stateSelectAction: addStates,
            cities,
            citySelectAction: addCities,
            townships,
            townshipSelectAction: addTownships,
            // disabled
            cityDisabled: storedStates.length <= 0,
            townshipDisabled: storedCities.length <= 0,
            // selector ids
            divisionSelectId: storedStates[0]?.state_id,
            citySelectId: storedCities[0]?.city_id,
            townshipSelectId: storedTownships[0]?.township_id,
        });
    }, [
        stateData,
        cities,
        townships,
        storedStates,
        storedCities,
        storedTownships,
    ]);

  

    const asyncDispatcher = useCallback(
        async (reqBody: any) => {
            try {
                const rest = removeKeys(
                    reqBody,
                    'state_id',
                    'state_name',
                    'city_id',
                    'city_name',
                    'township_id',
                    'township_name'
                );

                const res = await createSupplier({
                    ...rest,
                    supplier_division_id: reqBody.state_id,
                    supplier_city_id: reqBody.city_id,
                    supplier_township_id: reqBody.township_id,
                });

                const successData = 'data' in res ? res.data : null;
                const { success, message } = successData as TSuccessResponse;

                if (success || isSuccess) {
                    await Toast.fire({
                        title: message,
                        icon: 'success',
                    });

                    navigate(`/supplier-management${SUPPLIER.BASE_PATH}`, {
                        state: {
                            reload: true,
                        },
                    });
                }
            } catch (error) {
                console.log('Error', error);
            }
        },
        [createSupplier, isSuccess, Toast, navigate]
    );

    return (
        <PageWrapper>
            {isLoading && <LoadingSpinner />}
            <CustomForm
                title="Add New Supplier"
                schema={supplierSchema}
                fields={fields}
                asyncDispatcher={asyncDispatcher}
            />
        </PageWrapper>
    );
};

export default SupplierAddPage;
